import React, { useState } from 'react';

const SubmitFooter = ({
  isLocked,
  submitting,
  canSubmit,
  submitReason,
  pendingUploads = 0,
  failedUploads = 0,
  onOpenConfirm,
  onGoDashboard,
  lastSaved,
  theme,
}) => {
  const [showHoverTip, setShowHoverTip] = useState(false);

  const disabled = isLocked || submitting || !canSubmit;

  const tooltipItems = [];

  if (!isLocked && disabled) {
    if (submitReason) {
      tooltipItems.push({
        icon: '📝',
        text: submitReason,
      });
    }

    if (pendingUploads > 0) {
      tooltipItems.push({
        icon: '⏳',
        text: `${pendingUploads} upload${
          pendingUploads !== 1 ? 's are' : ' is'
        } still in progress`,
      });
    }

    if (failedUploads > 0) {
      tooltipItems.push({
        icon: '⚠️',
        text: `${failedUploads} upload${
          failedUploads !== 1 ? 's have' : ' has'
        } failed. Please retry before submitting.`,
      });
    }
  }

  const showTooltip =
    showHoverTip &&
    !isLocked &&
    disabled &&
    tooltipItems.length > 0;

  return (
    <>
      <div
        className={`sticky bottom-0 z-40 shadow-lg p-4 rounded-lg flex gap-3 justify-center flex-wrap ${theme.panel}`}
      >
        {/* Submit button */}
        <div
          className="relative inline-block"
          onMouseEnter={() => setShowHoverTip(true)}
          onMouseLeave={() => setShowHoverTip(false)}
          onFocus={() => setShowHoverTip(true)}
          onBlur={() => setShowHoverTip(false)}
        >
          <button
            type="button"
            disabled={disabled}
            onClick={onOpenConfirm}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              disabled
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {isLocked
              ? '✅ Submitted'
              : submitting
              ? 'Submitting...'
              : '🔒 Submit & Lock'}
          </button>

          {showTooltip && (
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-3 z-50 w-80 rounded-lg bg-gray-900 text-white shadow-2xl border border-gray-700 p-4 text-sm">

              <div className="font-semibold text-white border-b border-gray-700 pb-2 mb-3">
                🔒 Cannot submit yet
              </div>

              <div className="space-y-2">
                {tooltipItems.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2"
                  >
                    <span>{item.icon}</span>
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>

              {lastSaved && (
                <div className="mt-3 pt-3 border-t border-gray-700 text-xs text-gray-300">
                  💾 Last saved: {lastSaved}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Dashboard */}
        <button
          type="button"
          onClick={onGoDashboard}
          className="px-6 py-3 rounded-lg font-semibold bg-amber-600 hover:bg-amber-700 text-white transition"
        >
          📊 Dashboard
        </button>
      </div>

      {!isLocked && (
        <div className="mt-2 text-center">
          <p className={`text-xs ${theme.subtext}`}>
            {canSubmit
              ? '✅ Ready for submission'
              : submitReason || 'Complete required steps before submitting.'}
          </p>

          {pendingUploads > 0 && (
            <p className="text-xs text-yellow-600 mt-1">
              ⏳ {pendingUploads} upload
              {pendingUploads !== 1 ? 's' : ''} still in progress
            </p>
          )}

          {failedUploads > 0 && (
            <p className="text-xs text-red-600 mt-1">
              ⚠️ {failedUploads} upload
              {failedUploads !== 1 ? 's have' : ' has'} failed. Retry before
              submitting.
            </p>
          )}
        </div>
      )}

      {lastSaved && (
        <p className={`text-xs text-center mt-1 ${theme.subtext}`}>
          Last saved: {lastSaved}
        </p>
      )}
    </>
  );
};

export default SubmitFooter;